import { GitBranch, Plus, X } from "lucide-react";
import { useMemo, type FC } from "react";

import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeShowIf
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  CONDITION_SOURCE_TYPES,
  showIfProblem
} from "@/features/foreignNationalOutgoingIntakes/conditional";
import { flattenIntakeStepNodes } from "@/features/foreignNationalOutgoingIntakes/intakeStepFlattening";

import { stepLabel } from "./stepModel";

type Props = {
  node: OutgoingIntakeFormNode;
  schema: OutgoingIntakeFormNode[];
  readOnly: boolean;
  onChange: (showIf: OutgoingIntakeShowIf | null) => void;
};

const firstValue = (
  source: OutgoingIntakeFormNode
): OutgoingIntakeShowIf["equals"] => {
  if (source.question_type === "boolean") return true;
  if (source.question_type === "short_text") return "";
  return String(source.options?.[0]?.option_id ?? "");
};

const containsNode = (step: OutgoingIntakeFormNode, id: string): boolean =>
  step.node_id === id || (step.children ?? []).some((c) => containsNode(c, id));

/** "Show this question only if <earlier question> is <value>". */
export const ShowIfField: FC<Props> = ({
  node,
  schema,
  readOnly,
  onChange
}) => {
  const steps = useMemo(() => flattenIntakeStepNodes(schema), [schema]);
  const sources = useMemo(() => {
    const own = steps.findIndex((s) => containsNode(s, node.node_id));
    const before = own >= 0 ? steps.slice(0, own) : steps;
    return before
      .map((s, i) => ({ node: s, number: i + 1 }))
      .filter(
        ({ node: s }) =>
          s.node_type === "question" &&
          CONDITION_SOURCE_TYPES.has(String(s.question_type))
      );
  }, [node.node_id, steps]);
  const rule = node.show_if?.question_id ? node.show_if : null;
  const source = rule
    ? sources.find((s) => s.node.node_id === rule.question_id)?.node
    : undefined;
  const problem = rule ? showIfProblem(node, schema) : null;

  if (!rule) {
    return (
      <Field>
        <FieldLabel className="text-xs">Show This Question</FieldLabel>
        {readOnly ? (
          <span className="text-xs text-muted-foreground">Always</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-fit justify-start px-2 text-xs"
            disabled={!sources.length}
            onClick={() => {
              const s = sources[sources.length - 1]?.node;
              if (s)
                onChange({ question_id: s.node_id, equals: firstValue(s) });
            }}
          >
            <Plus className="size-3" /> Only if an earlier answer is…
          </Button>
        )}
        {!sources.length && !readOnly ? (
          <FieldDescription className="text-xs">
            Conditions need an earlier choice, yes / no or short text question.
          </FieldDescription>
        ) : null}
      </Field>
    );
  }

  const equalsValue = Array.isArray(rule.equals)
    ? String(rule.equals[0] ?? "")
    : typeof rule.equals === "boolean"
    ? rule.equals
      ? "yes"
      : "no"
    : String(rule.equals ?? "");

  return (
    <Field>
      <FieldLabel className="text-xs">Show This Question</FieldLabel>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-fill-1 p-2 text-xs">
        <GitBranch className="size-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">Only if</span>
        <Select
          value={rule.question_id}
          disabled={readOnly}
          onValueChange={(id) => {
            const s = sources.find((x) => x.node.node_id === id)?.node;
            if (s) onChange({ question_id: id, equals: firstValue(s) });
          }}
        >
          <SelectTrigger
            size="sm"
            className="h-7 w-auto max-w-56 gap-1 text-xs"
            aria-label="Earlier question"
          >
            <SelectValue placeholder="A removed question" />
          </SelectTrigger>
          <SelectContent>
            {sources.map((s) => (
              <SelectItem
                key={s.node.node_id}
                value={s.node.node_id}
                className="text-xs"
              >
                {s.number} · {stepLabel(s.node)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">is</span>
        {source?.question_type === "short_text" ? (
          <Input
            value={String(rule.equals ?? "")}
            disabled={readOnly}
            onChange={(e) => onChange({ ...rule, equals: e.target.value })}
            aria-label="Expected answer"
            className="h-7 w-32 text-xs"
          />
        ) : (
          <Select
            value={equalsValue}
            disabled={readOnly || !source}
            onValueChange={(v) =>
              onChange({
                ...rule,
                equals:
                  source?.question_type === "boolean"
                    ? v === "yes"
                    : source?.question_type === "multi_select"
                    ? [v]
                    : v
              })
            }
          >
            <SelectTrigger
              size="sm"
              className="h-7 w-auto max-w-48 gap-1 text-xs"
              aria-label="Expected answer"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {source?.question_type === "boolean" ? (
                <>
                  <SelectItem value="yes" className="text-xs">
                    Yes
                  </SelectItem>
                  <SelectItem value="no" className="text-xs">
                    No
                  </SelectItem>
                </>
              ) : (
                (source?.options ?? [])
                  .filter((o) => String(o.label ?? "").trim())
                  .map((o) => (
                    <SelectItem
                      key={o.option_id}
                      value={String(o.option_id)}
                      className="text-xs"
                    >
                      {o.label}
                    </SelectItem>
                  ))
              )}
            </SelectContent>
          </Select>
        )}
        <span className="flex-1" />
        {!readOnly ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label="Always show"
            onClick={() => onChange(null)}
          >
            <X className="size-3" />
          </Button>
        ) : null}
      </div>
      <FieldDescription
        className={problem ? "text-xs text-destructive" : "text-xs"}
      >
        {problem
          ? `This condition ${problem}. Pick another question or remove it.`
          : "Skipped for clients who answer anything else, and left out of the review."}
      </FieldDescription>
    </Field>
  );
};
